/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import moment from 'moment';
import { BallotRequest, ServiceRequest } from '@src/app/model/serviceRequest';
import { PillColorDef, PillColors } from '@src/app/shared/form/form-pill/form-pill.component';
import { channelColour, channelIcon } from '@src/app/modules/social/channelPillFields';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { RequestService } from '@src/app/modules/crm/request.service';
import { BallotPageNavRoute } from '@src/app/routes/app-router';

/**
 * The Approval tile - whether the request has been escalated to a channel, the ballots/meetings it
 * is on the agenda of, and whether it is approved to be worked on. Drawn straight from the request
 * rather than through the page's fields, which stay on the page for the list view and docs extract.
 * Changing the channel and meeting agendas is the tile's Edit - see RequestAgendaDialogComponent.
 */
@Component({
    selector: 'app-request-approval-tile',
    templateUrl: './request-approval-tile.component.html',
    styleUrls: ['./request-approval-tile.component.scss'],
    imports: [DatePipe, RouterLink, MatIconModule, MatTooltipModule]
})
export class RequestApprovalTileComponent {
    private dataSvc = inject(RequestService);
    private cds = inject(ConfirmDialogService);

    @Input() sr: ServiceRequest;
    @Output() changed = new EventEmitter<ServiceRequest>();

    get channelPaint(): PillColorDef {
        return PillColors[channelColour(this.sr.forumPillColor)];
    }

    get channelIcon(): string {
        return channelIcon(this.sr.forumPillIcon);
    }

    get approvalLabel(): string {
        return this.sr.approvedAt ? $localize`Approved by ${this.sr.approvedByName}` : $localize`Approve Now`;
    }

    get approvalPaint(): PillColorDef {
        return this.sr.approvedAt ? PillColors.green : PillColors.lightGrey;
    }

    get approvalHint(): string {
        return this.sr.approvedAt
            ? $localize`You can click here to remove the approval`
            : $localize`You can click here to approve the request`;
    }

    get approvedFromNow(): string {
        return moment(this.sr.approvedAt * 1000).fromNow();
    }

    get approvedFullDate(): string {
        return moment(this.sr.approvedAt * 1000).format('LL');
    }

    ballotLink(link: BallotRequest) {
        return BallotPageNavRoute.getIdUrl(link.ballotId, link.teamId);
    }

    ballotClosed(link: BallotRequest) {
        return !!link.ballotClosesAt && link.ballotClosesAt * 1000 < Date.now();
    }

    toggleApproval() {
        if (this.sr.approvedAt) {
            this.unapprove();
        } else {
            this.approve();
        }
    }

    private approve() {
        const title = $localize`Mark this request approved for action?`;
        const message = $localize`You should only do this with the consent of all relevant parties. There will be an audit trail of the approval`;
        this.cds.open(title, message, () => {
            this.dataSvc.approveRequest(this.sr).subscribe(updated => this.emitIfSaved(updated));
        }, $localize`Approve Now`);
    }

    private unapprove() {
        const title = $localize`Remove the approval for the request to be carried out?`;
        const message = $localize`You should only do this with the consent of all relevant parties. There will be an audit trail of the removal`;
        this.cds.open(title, message, (reason) => {
            this.dataSvc.unapproveRequest(this.sr, reason).subscribe(updated => this.emitIfSaved(updated));
        }, $localize`Unapprove`, true);
    }

    private emitIfSaved(updated: ServiceRequest) {
        if (updated) {
            this.changed.emit(updated);
        }
    }
}
